import React, { useEffect, useState } from 'react';
import Button from '@navi/web-ui/lib/primitives/Button/index';
import { AnswerView } from '../../interfaces/CommunicationHistory.type';
import ViewFeedbackForm from './ViewFeedbackForm';
import Drawer from '@navi/web-ui/lib/primitives/Drawer/Drawer';
import AddCustomerFeedback from './AddCustomerFeedback';
import { InteractionType } from '../interfaces/index.type';
import { useParams, useSearchParams } from 'react-router-dom';
import { NESTED_BLANK_DISPOSITION_QUERY } from '../../constants/Interactions.constant';
import { readQueryParams } from 'src/utils/QueryParamsHelper';
import { PubSub, TOPICS } from '../../../../utils/pubSub';
import { addClickstreamEvent } from '../../../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../../../service/clickStream.constant';
import { useDispatch, useSelector } from 'react-redux';
import { clearInteraction, resetFeedbackQuestionTypes } from '../reducer/feedbackSlice';
import { ArrowUpSolidIcon } from '@navi/web-ui/lib/icons';
import cx from 'classnames';
import styles from '../index.module.scss';
import { AmeyoNonConnectedDispositionProps } from '../../components/CommunicationHistory/type';
import { CALL_INTERACTIONS_TYPE } from '../../constants/communicationHistory.constant';
import { RootState } from '@cp/src/store';
import { createKey } from '@cp/utils/CaseDetail.utils';
import { Roles } from '@cp/pages/auth/constants/AuthConstants';

interface FeedbackActionButtonProps {
  answerViews: AnswerView[];
  referenceId: string;
  interactionType: InteractionType;
  comments: string;
  ameyoNonConnectedInteractionsInfo: AmeyoNonConnectedDispositionProps[];
  toggleState: boolean;
  interactionData: any;
}

const FeedbackActionButton: React.FC<FeedbackActionButtonProps> = props => {
  const {
    answerViews,
    referenceId,
    interactionType,
    comments,
    ameyoNonConnectedInteractionsInfo,
    toggleState,
    interactionData
  } = props;

  const [showModal, setShowModal] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { loanId = '', customerId = '' } = useParams();
  const dispatch = useDispatch();

  const params = readQueryParams();

  const feedbackExists: boolean = answerViews?.length > 0;
  const isMissedCall: boolean = (interactionType as string) === CALL_INTERACTIONS_TYPE.INBOUND_CALL;
  const isMissedStatusInboundCall: boolean =
    interactionData?.metadata?.status === 'Missed' &&
    interactionData?.metadata?.callType === 'INBOUND';
  const { editAccessFlag, user } = useSelector((state: RootState) => ({
    editAccessFlag:
      state?.caseDetail?.pageData?.[createKey(loanId, customerId)]?.details?.data?.editAccessFlag,
    user: state?.common?.userData
  }));
  const isGlobalAccessRoleGiven = user?.roles?.includes(Roles.ROLE_GLOBAL_ACCESS);
  const disableCTAs = isGlobalAccessRoleGiven ? !editAccessFlag : false;

  const removeQueryParams = (query: string) => {
    const param = searchParams.get(query);
    if (param) {
      searchParams.delete(query);
      setSearchParams(searchParams);
    }
  };

  const toggleModal = () => setShowModal(!showModal);
  const toggleDrawer = () => {
    dispatch(clearInteraction({}));
    setShowDrawer(!showDrawer);
  };
  const removeInteractionIdAndToggle = () => {
    removeQueryParams(NESTED_BLANK_DISPOSITION_QUERY);
    dispatch(resetFeedbackQuestionTypes());
    toggleDrawer();
  };
  const [queryParams] = useSearchParams();

  useEffect(() => {
    const interactionId = params?.blankDisposition?.interactionId || '';
    if (interactionId && interactionId === referenceId) {
      toggleDrawer();
    }
  }, [queryParams]);

  return (
    <>
      {(!feedbackExists && !isMissedCall) || isMissedStatusInboundCall ? (
        <div className={styles.actionWrapper}>
          <Button
            onClick={() => {
              if (disableCTAs) return;
              toggleDrawer();
              addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_CallsViewCTA, {
                interactionId: referenceId
              });
            }}
            variant="text"
            disabled={
              ameyoNonConnectedInteractionsInfo?.length > 0 ||
              disableCTAs ||
              isMissedStatusInboundCall
            }
          >
            Add
          </Button>
          {ameyoNonConnectedInteractionsInfo?.length > 1 && (
            <>
              <div className={styles.countStyles}>
                ({ameyoNonConnectedInteractionsInfo?.length})
              </div>
              <ArrowUpSolidIcon
                width={10}
                className={cx(styles.arrowSolidIcon, { [styles.rotateIcon]: !toggleState })}
              />
            </>
          )}
        </div>
      ) : (
        <div className={styles.actionWrapper}>
          <Button
            onClick={() => {
              toggleModal();
              addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_CallsViewCTA, {
                interactionId: referenceId
              });
            }}
            variant="text"
            disabled={ameyoNonConnectedInteractionsInfo?.length > 0 || isMissedCall}
          >
            View
          </Button>
          {ameyoNonConnectedInteractionsInfo?.length > 1 && (
            <>
              <div className={styles.countStyles}>
                ({ameyoNonConnectedInteractionsInfo?.length})
              </div>
              <ArrowUpSolidIcon
                width={10}
                className={cx(styles.arrowSolidIcon, { [styles.rotateIcon]: !toggleState })}
              />
            </>
          )}
        </div>
      )}
      {showModal ? (
        <ViewFeedbackForm
          open={showModal}
          onClose={toggleModal}
          answers={answerViews}
          comments={comments}
          interactionData={interactionData}
        />
      ) : (
        <></>
      )}
      {showDrawer && (
        <Drawer
          open={showDrawer}
          onClose={removeInteractionIdAndToggle}
          headerText="Add customer feedback"
          showHeaderDivider={true}
          contentClasses="overflow-auto"
          usePadding={false}
        >
          <AddCustomerFeedback
            showDrawer={showDrawer}
            referenceId={referenceId}
            interactionType={interactionType}
            onClose={toggleDrawer}
            customerId={customerId}
            loanId={loanId}
            feedbackSubmitEnabled
            interactionData={interactionData}
          />
        </Drawer>
      )}
    </>
  );
};

export default FeedbackActionButton;
